import type {
  FlightPageOption,
  FlightPageSegment,
  FlightPageSlice
} from '../client'

// ISO 8601 duration → minutes, or null when the value is missing or
// unparsable. Duffel writes anything over a day as "P1DT8H45M", so the day
// field is as load-bearing as the hours: reading it as zero put every
// long-haul connection at the top of the "Fastest" sort.
export function durationMinutes(value: string | null | undefined): number | null {
  if (!value) {
    return null
  }
  const match = /^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/.exec(value)
  if (!match || match.slice(1).every((g) => g === undefined)) {
    return null
  }
  // Seconds count towards the total rather than being dropped — an ignored
  // "PT45S" would read as a known zero-minute trip, which is the very thing
  // this parser exists to stop.
  const [days = 0, hours = 0, minutes = 0, seconds = 0] = match
    .slice(1, 5)
    .map((g) => Number(g ?? 0))
  return Math.round(days * 24 * 60 + hours * 60 + minutes + seconds / 60)
}

// Segment stamps are local wall-clock at their airport and carry no offset,
// so they are anchored to UTC before any arithmetic. Reading them in the
// viewer's zone instead would stretch or shrink a layover that straddles the
// VIEWER's own daylight-saving switch, which has nothing to do with the trip.
function wallClockMinutes(iso: string | null | undefined): number | null {
  if (!iso) {
    return null
  }
  const anchored = /(?:Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`
  const ms = new Date(anchored).getTime()
  return Number.isNaN(ms) ? null : Math.round(ms / 60_000)
}

// A layover is the gap between one segment's arrival and the next's
// departure. Both stamps are wall-clock at the same airport, so the
// difference is exact.
function layoverMinutes(prev: FlightPageSegment, next: FlightPageSegment): number | null {
  const arrive = wallClockMinutes(prev.arriving_at)
  const depart = wallClockMinutes(next.departing_at)
  return arrive === null || depart === null ? null : Math.max(0, depart - arrive)
}

// Total slice time: the slice's own duration, else segment flight times plus
// layovers (LiteAPI offers carry durations on segments only). Null when
// neither is known.
export function sliceMinutes(slice: FlightPageSlice): number | null {
  const own = durationMinutes(slice.duration)
  if (own !== null) {
    return own
  }
  const segments = slice.segments ?? []
  if (segments.length === 0) {
    return null
  }
  let total = 0
  let previous: FlightPageSegment | null = null
  for (const segment of segments) {
    const flight = durationMinutes(segment.duration)
    const layover = previous ? layoverMinutes(previous, segment) : 0
    if (flight === null || layover === null) {
      return null
    }
    total += flight + layover
    previous = segment
  }
  return total
}

export function tripMinutes(option: FlightPageOption): number | null {
  const slices = option.slices ?? []
  if (slices.length === 0) {
    return null
  }
  let total = 0
  for (const slice of slices) {
    const mins = sliceMinutes(slice)
    if (mins === null) {
      return null
    }
    total += mins
  }
  return total
}

// `stops` is copied verbatim through the same untrusted path as the durations
// and defaults to 0 on the backend model, so a slice whose segment list says
// otherwise is resolved the safe way: a connection is never shown as direct.
export function sliceStops(slice: FlightPageSlice): number {
  return Math.max(slice.stops ?? 0, (slice.segments ?? []).length - 1)
}

// An option with no itinerary detail cannot be shown as direct — "every stop
// of nothing is zero" would let it through the filter.
export function isDirect(option: FlightPageOption): boolean {
  const slices = option.slices ?? []
  return slices.length > 0 && slices.every((s) => sliceStops(s) === 0)
}

export function firstDepartureMs(option: FlightPageOption): number | null {
  const mins = wallClockMinutes(option.slices?.[0]?.departing_at)
  return mins === null ? null : mins * 60_000
}

// Unknown values sort after every known one, whatever the direction.
export function compareKnownFirst(a: number | null, b: number | null): number {
  if (a === null || b === null) {
    return Number(a === null) - Number(b === null)
  }
  return a - b
}

// "5h 30m". An unknown or non-positive duration renders as nothing at all
// rather than as a zero, and never as the raw ISO string.
export function formatMinutes(total: number | null): string {
  if (total === null || total <= 0) {
    return ''
  }
  const h = Math.floor(total / 60)
  const m = total % 60
  return [h ? `${h}h` : '', m ? `${m}m` : ''].filter(Boolean).join(' ')
}

export function formatDuration(value: string | null | undefined): string {
  return formatMinutes(durationMinutes(value))
}
